import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import Breakpoints from 'themes/constants/breakpoints';
import { checkIsIOS, checkIsInStandaloneMode } from 'helpers/platform';
import Portal from 'components/Portal/Portal';
import useAddToHomeScreen from 'hooks/useAddToHomeScreen';
import { ReactComponent as XIcon } from 'assets/Icons/x.svg';
import { ReactComponent as IOSMenuIcon } from 'assets/Icons/ios-menu.svg';

const WrapBase = styled.div`
    font-family: -apple-system, BlinkMacSystemFont, Nunito, Roboto, "Helvetica Neue", sans-serif;
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    width: 460px;
    background: #fff;
    color: #333;
    box-shadow: 0 0 30px -5px rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    z-index: 1000;
    box-sizing: border-box;
    @media only screen and (${Breakpoints.LAPTOP}) {
        width: 380px;
        right: 20px;
        left: unset;
        bottom: 20px;
        transform: unset;
    }
`;
const WrapInstallApp = styled(WrapBase)`
    bottom: 10px;
    max-width: calc(100vw - 20px);
    padding: 6px 40px 6px 6px;
    height: 62px;
`;
const WrapIOS = styled(WrapBase)`
    bottom: 15px;
    max-width: calc(100vw - 30px);
    padding: 15px 40px 15px 15px;
    height: 75px;
`;
const ImageBase = styled.img.attrs({ alt: '' })`
    flex-shrink: 0;
    border-radius: 5px;
    background: #d9d9d9;
`;
const ImageInstallApp = styled(ImageBase)`
    width: 50px;
    height: 50px;
    margin-right: 6px;
`;
const ImageIOS = styled(ImageBase)`
    width: 45px;
    height: 45px;
    margin-right: 15px;
`;
const TextBase = styled.div`
    flex-grow: 1;
    font-weight: 400;
    font-size: 10px;
    user-select: none;
    @media only screen and (${Breakpoints.TABLET_S}) {
        font-size: 12px;
        line-height: 1.2;
    }
`;
const TextInstallApp = styled(TextBase)`
    line-height: 1.1;
    margin-right: 10px;
`;
const TextIOS = styled(TextBase)`
    line-height: 1.2;
    & svg {
        width: 13px;
        height: 13px;
        margin: 0 2px -2px;
    }
`;
const ButtonBase = styled.button.attrs({ type: 'button' })`
    border: none;
    outline: none;
    text-transform: none;
    cursor: pointer;
    margin: 0;
    padding: 0;
`;
const InstallAppButton = styled(ButtonBase)`
    flex-shrink: 0;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.16;
    background: #6cba58;
    color: #333;
    height: 30px;
    padding: 8px 10px;
    margin-left: auto;
    border-radius: 5px;
`;
const CloseButtonBase = styled(ButtonBase)`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    color: #000;
    background: #f2f2f2;
    border-radius: 100%;
    & > svg {
        width: 18px;
        height: 18px;
    }
`;
const CloseButtonInstallApp = styled(CloseButtonBase)`
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
`;
const CloseButtonIOS = styled(CloseButtonBase)`
    top: 10px;
    right: 10px;
`;

function AddToHomeScreen() {
  const isLaptop = useMediaQuery({ query: `(${Breakpoints.LAPTOP})` });

  const [show, setShow] = useState<boolean>(false);
  const [canShow, setPromptCanShow] = useState<boolean>(false);
  const { prompt, promptToInstall } = useAddToHomeScreen();

  const canInstallApp = !!prompt;
  const isIOS = checkIsIOS();
  const isInStandaloneMode = checkIsInStandaloneMode();
  const canShowModal = canShow && (canInstallApp || (isIOS && !isInStandaloneMode));

  const handleClose = () => setShow(false);
  const handleInstall = () => {
    promptToInstall();
    handleClose();
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = document.documentElement.scrollTop;
      const availHeight = window.screen?.availHeight ?? 0;

      if (scrollPosition >= availHeight) setPromptCanShow(true);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (canShowModal) setShow(true);
  }, [canShowModal]);

  const homePageName = () => {
    if (isLaptop) return 'рабочий стол';
    return 'домашний экран';
  };

  const serviceName = 'BeHealthy';
  const installAppText = `Добавить ярлык «${serviceName}» на ${homePageName()}?`;

  const imgSrc = `${process.env.PUBLIC_URL}/icon-192x192.png`;

  const installAppModalEl = (
    <WrapInstallApp onClick={handleInstall}>
      <ImageInstallApp src={imgSrc} />
      <TextInstallApp>{installAppText}</TextInstallApp>
      <InstallAppButton>Добавить</InstallAppButton>
      <CloseButtonInstallApp
        onClick={(ev) => {
          ev.stopPropagation();
          handleClose();
        }}
      >
        <XIcon />
      </CloseButtonInstallApp>
    </WrapInstallApp>
  );
  const IOSModalEl = (
    <WrapIOS>
      <ImageIOS src={imgSrc} />
      <TextIOS>
        <div>{installAppText}</div>
        <div>
          Нажмите на
          <IOSMenuIcon />
          и выберите «На экран «Домой»
        </div>
      </TextIOS>
      <CloseButtonIOS onClick={handleClose}>
        <XIcon />
      </CloseButtonIOS>
    </WrapIOS>
  );

  const modalEl = () => {
    if (canInstallApp) return installAppModalEl;
    if (isIOS) return IOSModalEl;
    return null as any;
  };

  if (show) {
    return (
      <Portal>
        {modalEl()}
      </Portal>
    );
  }
  return null;
}

export default AddToHomeScreen;
